import { useCallback, useState } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { checkpointsService } from '@/api/services/checkpoints.service'

interface ResetCheckpointButtonProps {
  spreadId: string | null
}

/** Dev-only rail button: discards the spread's user layer and reloads. */
export function ResetCheckpointButton({ spreadId }: ResetCheckpointButtonProps) {
  const [isResetting, setIsResetting] = useState(false)

  const handleResetCheckpoint = useCallback(async () => {
    if (!spreadId) return

    const confirmed = window.confirm(
      'Are you sure you want to reset THIS spread?\n\nThis discards every edit on the user layer, re-pulls the document from the upstream system, and recomposes it. This action cannot be undone.'
    )

    if (!confirmed) return

    setIsResetting(true)
    try {
      await checkpointsService.resetCheckpoint(spreadId)
      toast.success('Checkpoint reset — reloading…')
      // Server-side state is now clean; reload so the canvas matches it.
      // Keep isResetting true so the button stays disabled through the reload.
      setTimeout(() => window.location.reload(), 600)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'

      toast.error(`Failed to reset checkpoint: ${message}`)
      setIsResetting(false)
    }
  }, [spreadId])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleResetCheckpoint}
          disabled={isResetting || !spreadId}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          aria-label="Reset spread checkpoint"
        >
          {isResetting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">
        <p>Reset Spread Checkpoint</p>
      </TooltipContent>
    </Tooltip>
  )
}
